/**
 * @fileoverview
 * Utility for defining and managing content types used across the app.
 *
 * This module centralizes the definition of available content types (from Strapi),
 * and provides a consistent mapping of their singular and plural forms.
 *
 *
 * ```ts
 * import { CONTENT_TYPES, contentTypeMap } from '@/utils/content-types';
 *
 * const { singular, plural } = contentTypeMap[CONTENT_TYPES.PODCASTS];
 * ```
 */

/**
 * Enum representing all available content types.
 *
 * These string values match the identifiers used in Strapi
 */
export const CONTENT_TYPES = {
  ARTIKELS: 'artikels',
  NEWS: 'news',
  FLASH_TALKS: 'flash-talks',
  PODCASTS: 'podcasts',
  EDU_TALKS: 'edu-talks',
  RATGEBERS: 'ratgebers',
  WEBINARS: 'webinars',
  USE_CASES: 'use-cases',
} as const;

/**
 * Mapping of each content type to its singular and plural forms.
 *
 * Using `Record<CONTENT_TYPES, { singular: string; plural: string }>` ensures:
 *  - All content types in the enum are represented.
 *  - The mapping is type-safe and cannot include invalid keys.
 */
export const contentTypeMap: Record<
  (typeof CONTENT_TYPES)[keyof typeof CONTENT_TYPES],
  { singular: string; plural: string }
> = {
  [CONTENT_TYPES.ARTIKELS]: { singular: 'artikel', plural: 'artikels' },
  [CONTENT_TYPES.NEWS]: { singular: 'new', plural: 'news' },
  [CONTENT_TYPES.FLASH_TALKS]: {
    singular: 'flash-talk',
    plural: 'flash-talks',
  },
  [CONTENT_TYPES.PODCASTS]: { singular: 'podcast', plural: 'podcasts' },
  [CONTENT_TYPES.EDU_TALKS]: { singular: 'edu-talk', plural: 'edu-talks' },
  [CONTENT_TYPES.RATGEBERS]: { singular: 'ratgeber', plural: 'ratgebers' },
  [CONTENT_TYPES.WEBINARS]: { singular: 'webinar', plural: 'webinars' },
  [CONTENT_TYPES.USE_CASES]: { singular: 'use-case', plural: 'use-cases' },
};

/**
 * Route segments that should use article-like layouts.
 *
 * This is intentionally explicit because some route folders differ from the
 * singular Strapi type naming, e.g. `news` uses `/news/` rather than `/new/`.
 */
export const ARTICLE_LIKE_ROUTE_SEGMENTS = [
  'artikel',
  'news',
  'flash-talk',
  'podcast',
  'edu-talk',
  'ratgeber',
  'webinar',
  'use-case',
] as const;

/**
 * Type alias for convenience — represents all possible content type values.
 */
export type ContentType = (typeof CONTENT_TYPES)[keyof typeof CONTENT_TYPES];
export type ContentTypes = ContentType;

/**
 * Finds a content type config by either its singular Strapi model key or its
 * plural frontend/API key.
 *
 * Examples:
 *  - `artikel` -> `{ singular: 'artikel', plural: 'artikels' }`
 *  - `artikels` -> `{ singular: 'artikel', plural: 'artikels' }`
 */
const findContentTypeConfig = (type: string) =>
  Object.values(contentTypeMap).find(
    (config) => config.singular === type || config.plural === type,
  );

/**
 * Converts any known content type value to the singular Strapi model key.
 *
 * Used for aggregate endpoints like `/api/web/content-hub`, which query models
 * by singular names such as `artikel`, `new`, or `flash-talk`.
 */
export const getContentTypeSingular = (type: string): string => {
  return findContentTypeConfig(type)?.singular ?? type;
};

/**
 * Converts any known content type value to the plural frontend/API key.
 *
 * Used for web-facing routes such as `/api/web/collections/:type`,
 * where values are `artikels`, `news`, `flash-talks`, etc.
 */
export const getContentTypePlural = (type: string): string => {
  return findContentTypeConfig(type)?.plural ?? type;
};
